import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { Calendar, Users, Mail, Phone, CreditCard, Filter, Download, Eye, Printer } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBookingsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/bookings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/events`)
      ]);
      setBookings(bookingsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const getEventTitle = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event ? event.title : 'Événement inconnu';
  };

  const getEventDetails = (eventId) => {
    return events.find(e => e.id === eventId);
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesEvent = selectedEvent === 'all' || booking.event_id === selectedEvent;
    const matchesSearch = 
      booking.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesEvent && matchesSearch;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'Confirmé', color: 'bg-green-100 text-green-800' },
      paid: { label: 'Payé', color: 'bg-blue-100 text-blue-800' },
      cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-800' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = () => {
    const headers = ['Nom', 'Email', 'Téléphone', 'Événement', 'Participants', 'Prix Total', 'Statut', 'Date'];
    const rows = filteredBookings.map(b => [
      b.name,
      b.email,
      b.phone || '-',
      getEventTitle(b.event_id),
      b.participants,
      `${b.total_price} XPF`,
      b.payment_status,
      formatDate(b.created_at)
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `reservations_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Export CSV téléchargé');
  };

  const printList = () => {
    const eventTitle = selectedEvent === 'all' 
      ? 'Tous les événements' 
      : getEventTitle(selectedEvent);
    
    const eventDetails = selectedEvent !== 'all' ? getEventDetails(selectedEvent) : null;
    
    const printWindow = window.open('', '_blank');
    const totalParticipants = filteredBookings.reduce((sum, b) => sum + b.participants, 0);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Liste des réservations - ${eventTitle}</title>
        <style>
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            padding: 40px;
            color: #333;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 2px solid #8B4B7C;
          }
          .header h1 {
            color: #8B4B7C;
            margin: 0 0 10px 0;
            font-size: 24px;
          }
          .header h2 {
            color: #3D2E35;
            margin: 0 0 10px 0;
            font-size: 20px;
          }
          .header .event-info {
            color: #666;
            font-size: 14px;
          }
          .summary {
            background: #FAF8F5;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
            display: flex;
            justify-content: space-around;
          }
          .summary-item {
            text-align: center;
          }
          .summary-item .number {
            font-size: 24px;
            font-weight: bold;
            color: #8B4B7C;
          }
          .summary-item .label {
            font-size: 12px;
            color: #666;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 12px 8px;
            text-align: left;
          }
          th {
            background: #8B4B7C;
            color: white;
            font-weight: 500;
          }
          tr:nth-child(even) {
            background: #f9f9f9;
          }
          tr:hover {
            background: #f5f5f5;
          }
          .footer {
            margin-top: 30px;
            text-align: center;
            font-size: 12px;
            color: #999;
          }
          @media print {
            body { padding: 20px; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🌺 Tevaite Création Florale</h1>
          <h2>${eventTitle}</h2>
          ${eventDetails ? `
            <div class="event-info">
              📅 ${new Date(eventDetails.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              &nbsp;|&nbsp;
              📍 ${eventDetails.location}
            </div>
          ` : ''}
        </div>
        
        <div class="summary">
          <div class="summary-item">
            <div class="number">${filteredBookings.length}</div>
            <div class="label">Réservations</div>
          </div>
          <div class="summary-item">
            <div class="number">${totalParticipants}</div>
            <div class="label">Participants</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Nom (Contact)</th>
              <th>Email</th>
              <th>Téléphone</th>
              <th>Participants</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            ${filteredBookings.map((b, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>
                  <strong>${b.name}</strong>
                  ${b.participant_names && b.participant_names.length > 0 ? `
                    <div style="font-size: 12px; color: #666; margin-top: 4px;">
                      ${b.participant_names.map((name, i) => `<div>• ${name}</div>`).join('')}
                    </div>
                  ` : ''}
                </td>
                <td>${b.email}</td>
                <td>${b.phone || '-'}</td>
                <td style="text-align: center;">${b.participants}</td>
                <td>${b.payment_status === 'paid' ? '✅ Payé' : b.payment_status === 'confirmed' ? '✔️ Confirmé' : '⏳ En attente'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          Liste générée le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Group bookings by event for summary
  const bookingsByEvent = events.map(event => {
    const eventBookings = bookings.filter(b => b.event_id === event.id);
    const totalParticipants = eventBookings.reduce((sum, b) => sum + b.participants, 0);
    return {
      ...event,
      bookingsCount: eventBookings.length,
      totalParticipants
    };
  }).filter(e => e.bookingsCount > 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#3D2E35]">Réservations</h1>
            <p className="text-[#6B5A62] mt-1">Gérez les inscriptions aux événements et workshops</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={printList}
              variant="outline"
              className="flex items-center gap-2"
              disabled={filteredBookings.length === 0}
            >
              <Printer size={18} />
              Imprimer la liste
            </Button>
            <Button
              onClick={exportToCSV}
              variant="outline"
              className="flex items-center gap-2"
              disabled={filteredBookings.length === 0}
            >
              <Download size={18} />
              Exporter CSV
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {bookingsByEvent.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bookingsByEvent.map(event => (
              <div 
                key={event.id}
                className="bg-white rounded-xl p-4 border border-[#E8A5C4]/20 hover:border-[#C45B9F]/30 transition-colors cursor-pointer"
                onClick={() => setSelectedEvent(event.id)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-[#C45B9F] font-medium">
                      {event.type === 'workshop' ? 'Workshop' : 'Événement'}
                    </p>
                    <h3 className="font-heading text-lg text-[#3D2E35] mt-1 line-clamp-1">
                      {event.title}
                    </h3>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#8B4B7C]">{event.totalParticipants}</p>
                    <p className="text-xs text-[#6B5A62]">participants</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between text-sm text-[#6B5A62]">
                  <span>{event.bookingsCount} réservation(s)</span>
                  <span>{event.max_participants - event.current_participants} places restantes</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-[#E8A5C4]/30"
              />
            </div>
            <div className="w-full md:w-64">
              <Select value={selectedEvent} onValueChange={setSelectedEvent}>
                <SelectTrigger className="border-[#E8A5C4]/30">
                  <SelectValue placeholder="Filtrer par événement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les événements</SelectItem>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Bookings Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[#8B4B7C] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl">
            <Calendar className="mx-auto text-[#E8A5C4]" size={48} />
            <p className="text-[#6B5A62] mt-4">Aucune réservation trouvée</p>
            {selectedEvent !== 'all' && (
              <Button
                variant="link"
                onClick={() => setSelectedEvent('all')}
                className="mt-2 text-[#8B4B7C]"
              >
                Voir toutes les réservations
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAF8F5]">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#6B5A62]">Client</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#6B5A62]">Événement</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-[#6B5A62]">Participants</th>
                    <th className="px-6 py-4 text-right text-sm font-medium text-[#6B5A62]">Prix</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-[#6B5A62]">Statut</th>
                    <th className="px-6 py-4 text-center text-sm font-medium text-[#6B5A62]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8A5C4]/10">
                  {filteredBookings.map((booking) => (
                    <tr key={booking.id} className="hover:bg-[#FAF8F5]/50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-[#3D2E35]">{booking.name}</p>
                          <p className="text-sm text-[#6B5A62]">{booking.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-[#3D2E35]">{getEventTitle(booking.event_id)}</p>
                        <p className="text-xs text-[#6B5A62]">
                          {formatDate(booking.created_at)}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="inline-flex items-center gap-1 text-[#3D2E35]">
                          <Users size={16} className="text-[#C45B9F]" />
                          {booking.participants}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="font-medium text-[#3D2E35]">
                          {booking.total_price?.toLocaleString()} XPF
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {getStatusBadge(booking.payment_status)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedBooking(booking)}
                          className="text-[#8B4B7C] hover:text-[#C45B9F]"
                        >
                          <Eye size={18} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Booking Detail Modal */}
        <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[#3D2E35]">
                Détails de la réservation
              </DialogTitle>
            </DialogHeader>
            {selectedBooking && (
              <div className="space-y-6">
                {/* Client Info */}
                <div className="bg-[#FAF8F5] rounded-xl p-4 space-y-3">
                  <h3 className="font-medium text-[#8B4B7C]">Informations client</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[#6B5A62]">Nom</p>
                      <p className="font-medium text-[#3D2E35]">{selectedBooking.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#6B5A62]">Email</p>
                      <p className="font-medium text-[#3D2E35]">{selectedBooking.email}</p>
                    </div>
                    {selectedBooking.phone && (
                      <div>
                        <p className="text-sm text-[#6B5A62]">Téléphone</p>
                        <p className="font-medium text-[#3D2E35]">{selectedBooking.phone}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Event Info */}
                <div className="bg-[#FAF8F5] rounded-xl p-4 space-y-3">
                  <h3 className="font-medium text-[#8B4B7C]">Événement</h3>
                  <p className="font-heading text-lg text-[#3D2E35]">
                    {getEventTitle(selectedBooking.event_id)}
                  </p>
                  {getEventDetails(selectedBooking.event_id) && (
                    <p className="text-sm text-[#6B5A62]">
                      {new Date(getEventDetails(selectedBooking.event_id).date).toLocaleDateString('fr-FR', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  )}
                </div>

                {/* Booking Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#FAF8F5] rounded-xl p-4 text-center">
                    <Users className="mx-auto text-[#C45B9F] mb-2" size={24} />
                    <p className="text-2xl font-bold text-[#3D2E35]">{selectedBooking.participants}</p>
                    <p className="text-sm text-[#6B5A62]">participant(s)</p>
                  </div>
                  <div className="bg-[#FAF8F5] rounded-xl p-4 text-center">
                    <CreditCard className="mx-auto text-[#C45B9F] mb-2" size={24} />
                    <p className="text-2xl font-bold text-[#3D2E35]">
                      {selectedBooking.total_price?.toLocaleString()}
                    </p>
                    <p className="text-sm text-[#6B5A62]">XPF</p>
                  </div>
                </div>

                {/* Participant Names */}
                {selectedBooking.participant_names && selectedBooking.participant_names.length > 0 && (
                  <div className="bg-[#FAF8F5] rounded-xl p-4 space-y-2">
                    <h3 className="font-medium text-[#8B4B7C]">Liste des participants</h3>
                    <ul className="space-y-1">
                      {selectedBooking.participant_names.map((name, index) => (
                        <li key={index} className="text-[#3D2E35] flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-[#E8A5C4]/30 flex items-center justify-center text-xs text-[#8B4B7C]">
                            {index + 1}
                          </span>
                          {name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Status */}
                <div className="flex items-center justify-between">
                  <span className="text-[#6B5A62]">Statut du paiement</span>
                  {getStatusBadge(selectedBooking.payment_status)}
                </div>

                {/* Reservation date */}
                <div className="text-center text-sm text-[#6B5A62]">
                  Réservation effectuée le {formatDate(selectedBooking.created_at)}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminBookingsPage;
